// components/SubscriptionTable.jsx
import React, { useState } from 'react';
import { 
  Eye, Ban, Calendar, Settings, ChevronDown, 
  Gift, Plus, ChevronUp, CheckSquare, Square, Clock, 
  DollarSign, X, Users
} from 'lucide-react';
import StatusBadge from './StatusBadge';
import api from '../../api';
import { useToast } from './ToastContainer';

const SubscriptionTable = ({ 
  subscriptions, 
  plans,
  onManage, 
  onViewHistory,
  onExtendQuick, 
  onSetTrial,
  onSetSpecialPrice,
  onDeactivate,
  selectedTenants = [],
  onSelectTenant,
  onSelectAll,
  onRefresh
}) => {
  const { success, error: showError } = useToast();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const toggleDropdown = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const calculateDaysLeft = (endDate) => {
    if (!endDate) return 0;
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  // Unassign tenant from private plan
  const handleUnassign = async (tenantId, planId) => {
    if (!window.confirm('Remove this tenant from the private plan?')) return;
    
    setIsProcessing(true);
    try {
      await api.delete(`/api/super-admin/subscription/plans/${planId}/remove-tenant`, {
        data: { tenantId: tenantId }
      });
      success('Tenant removed from private plan');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('❌ Error unassigning:', err);
      showError(err.response?.data?.message || 'Failed to remove tenant');
    } finally {
      setIsProcessing(false);
      setOpenDropdown(null);
    }
  };

  // ✅ Remove special price - FIXED
  const handleRemoveSpecialPrice = async (tenantId, planId) => {
    if (!window.confirm('Are you sure you want to remove the special price for this tenant?')) return;
    
    setIsProcessing(true);
    try {
      console.log('🔍 Removing special price for:', { tenantId, planId });
      
      // ✅ Use DELETE with data payload
      const response = await api.delete(`/api/super-admin/subscription/tenant/${tenantId}/special-price`, {
        data: { planId: planId }
      });
      
      console.log('📥 Remove special price response:', response.data);
      
      if (response.data.success) {
        success('Special price removed successfully');
        // ✅ Refresh the data to reflect changes
        if (onRefresh) await onRefresh();
      } else {
        showError(response.data.message || 'Failed to remove special price');
      }
    } catch (err) {
      console.error('❌ Error removing special price:', err);
      const errorMsg = err.response?.data?.message || 'Failed to remove special price';
      showError(errorMsg);
    } finally {
      setIsProcessing(false);
      setOpenDropdown(null);
    }
  };

  // Check if all tenants are selected
  const allSelected = subscriptions.length > 0 && subscriptions.every(item => 
    selectedTenants.includes(item.tenantId)
  );

  if (subscriptions.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <div className="flex flex-col items-center gap-2">
          <Users className="w-12 h-12 text-slate-300" />
          <p className="text-sm text-slate-500">No tenants found</p>
          <p className="text-xs text-slate-400">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 w-10">
                <button
                  onClick={() => onSelectAll?.(!allSelected)}
                  className="text-slate-400 hover:text-indigo-600 transition-colors"
                >
                  {allSelected ? (
                    <CheckSquare className="w-4 h-4 text-indigo-600" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Tenant</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Plan</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Price</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Expires</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Days Left</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subscriptions.map((item) => {
              const hasSubscription = item.hasSubscription || !!item.subscription;
              const sub = item.subscription || item;
              const tenantName = item.organizationName || sub?.tenantName || item.tenantId;
              const planName = item.subscriptionPlanName || sub?.planName || sub?.planSnapshot?.name || 'No Plan';
              const status = item.subscriptionStatus || sub?.status || 'none';
              const price = sub?.actualPrice?.amount || 0;
              const endDate = item.subscriptionEndDate || sub?.endDate || null;
              const daysLeft = item.daysRemaining || calculateDaysLeft(endDate);
              const tenantId = item.tenantId || sub?.tenantId;
              const isNewTenant = item.isNewTenant || !hasSubscription;
              const displayStatus = !hasSubscription ? 'none' : status;
              const isSelected = selectedTenants.includes(tenantId);
              const hasSpecialPrice = sub?.actualPrice?.isDiscounted === true;

              // Check if plan is private
              const planVisibility = sub?.planSnapshot?.visibility || 'public';
              const isPrivate = planVisibility === 'private';
              const isAssigned = isPrivate && sub?.planSnapshot?.assignedTenants?.includes(tenantId);

              return (
                <tr key={item.tenantId || item.subscriptionId || `tenant-${Math.random()}`} className="hover:bg-slate-50 transition-colors">
                  {/* Checkbox */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onSelectTenant?.(tenantId)}
                      className="text-slate-400 hover:text-indigo-600 transition-colors"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-indigo-600" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{tenantName}</p>
                      <p className="text-xs text-slate-500">{tenantId}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-sm ${hasSubscription ? 'text-slate-700' : 'text-slate-400'}`}>
                        {hasSubscription ? planName : 'No Plan'}
                      </span>
                      {isPrivate && isAssigned && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-600 rounded-full border border-purple-200">
                          <Eye className="w-2.5 h-2.5" />
                          Private
                        </span>
                      )}
                      {hasSpecialPrice && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-600 rounded-full border border-emerald-200">
                          <DollarSign className="w-2.5 h-2.5" />
                          Special
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={displayStatus} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium ${price === 0 ? 'text-slate-500' : 'text-slate-900'}`}>
                      {!hasSubscription ? '-' : price === 0 ? 'Free' : `₹${price.toLocaleString()}`}
                    </span>
                    {hasSpecialPrice && (
                      <span className="ml-1 text-xs text-emerald-600">(Special)</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {!hasSubscription ? '-' : endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {status === 'active' || status === 'trial' ? (
                      <span className={`text-sm font-medium ${
                        daysLeft <= 7 ? 'text-rose-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {daysLeft} days
                      </span>
                    ) : !hasSubscription ? (
                      <span className="text-sm text-slate-400">-</span>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* History Button */}
                      <button
                        onClick={() => onViewHistory?.({
                          tenantId: tenantId,
                          tenantName: tenantName
                        })}
                        className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                        title="View History"
                      >
                        <Clock className="w-4 h-4 text-amber-500" />
                      </button>

                      {/* Manage Button */}
                      <button
                        onClick={() => onManage({
                          tenantId: tenantId,
                          tenantName: tenantName,
                          hasSubscription: hasSubscription,
                          planId: sub?.planId || null,
                          planName: planName,
                          status: status,
                          billingCycle: sub?.billingCycle || 'yearly',
                          actualPrice: sub?.actualPrice || { amount: 0 },
                          endDate: endDate,
                          subscription: sub,
                          isNewTenant: isNewTenant
                        })}
                        className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"
                        title={hasSubscription ? "Manage Subscription" : "Assign Plan"}
                      >
                        <Settings className="w-4 h-4 text-indigo-500" />
                      </button>

                      {/* Extend Button */}
                      {hasSubscription && (
                        <button
                          onClick={() => onExtendQuick(tenantId, 30)}
                          className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Extend 30 Days"
                        >
                          <Calendar className="w-4 h-4 text-blue-500" />
                        </button>
                      )}

                      {/* More Actions Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => toggleDropdown(`dropdown-${tenantId}`)}
                          className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                          title="More Actions"
                        >
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        </button>
                        
                        {openDropdown === `dropdown-${tenantId}` && (
                          <div className="absolute right-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-10 max-h-80 overflow-y-auto">
                            {/* ✅ ALWAYS SHOW SPECIAL PRICE OPTION */}
                            <button
                              onClick={() => {
                                onSetSpecialPrice({
                                  tenantId: tenantId,
                                  tenantName: tenantName,
                                  planId: sub?.planId || null,
                                  planName: planName,
                                  currentPrice: price,
                                  hasSpecialPrice: hasSpecialPrice,
                                  subscriptionStatus: status,
                                  daysRemaining: daysLeft,
                                  subscriptionId: sub?.subscriptionId,
                                  isOnTrial: status === 'trial',
                                  isActive: status === 'active',
                                  hasSubscription: hasSubscription
                                });
                                setOpenDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-emerald-600 hover:bg-slate-50 flex items-center gap-2"
                            >
                              <DollarSign className="w-4 h-4" />
                              {hasSpecialPrice ? 'Update Special Price' : 'Set Special Price'}
                            </button>

                            {/* ✅ REMOVE SPECIAL PRICE - Only show if special price exists */}
                            {hasSpecialPrice && (
                              <button
                                onClick={() => handleRemoveSpecialPrice(tenantId, sub?.planId)}
                                disabled={isProcessing}
                                className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                {isProcessing ? 'Removing...' : 'Remove Special Price'}
                              </button>
                            )}

                            <hr className="my-1 border-slate-100" />

                            {!hasSubscription ? (
                              <>
                                <button
                                  onClick={() => {
                                    onManage({
                                      tenantId: tenantId,
                                      tenantName: tenantName,
                                      hasSubscription: false,
                                      isNewTenant: true
                                    });
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-indigo-600 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Plus className="w-4 h-4" />
                                  Assign Plan
                                </button>
                                <button
                                  onClick={() => {
                                    onSetTrial(tenantId, 14);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Gift className="w-4 h-4" />
                                  Start Trial
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Start Trial */}
                                <button
                                  onClick={() => {
                                    onSetTrial(tenantId, 14);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Gift className="w-4 h-4" />
                                  Start Trial
                                </button>

                                {/* Extend Options */}
                                <hr className="my-1 border-slate-100" />
                                <button
                                  onClick={() => {
                                    onExtendQuick(tenantId, 30);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Calendar className="w-4 h-4 text-blue-500" />
                                  Extend 30 Days
                                </button>
                                <button
                                  onClick={() => {
                                    const days = prompt('Enter days to extend:', '30');
                                    if (days && parseInt(days) > 0) {
                                      onExtendQuick(tenantId, parseInt(days));
                                      setOpenDropdown(null);
                                    }
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Calendar className="w-4 h-4 text-blue-500" />
                                  Custom Extend
                                </button>

                                {/* Unassign from Private Plan */}
                                {isPrivate && isAssigned && (
                                  <button
                                    onClick={() => handleUnassign(tenantId, sub.planId)}
                                    disabled={isProcessing}
                                    className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <X className="w-4 h-4" />
                                    Unassign from Private Plan
                                  </button>
                                )}

                                <hr className="my-1 border-slate-100" />

                                {/* Deactivate Subscription */}
                                <button
                                  onClick={() => {
                                    onDeactivate(tenantId);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <Ban className="w-4 h-4" />
                                  Deactivate Subscription
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-slate-100">
        {subscriptions.map((item) => {
          const hasSubscription = item.hasSubscription || !!item.subscription;
          const sub = item.subscription || item;
          const tenantName = item.organizationName || sub?.tenantName || item.tenantId;
          const planName = item.subscriptionPlanName || sub?.planName || sub?.planSnapshot?.name || 'No Plan';
          const status = item.subscriptionStatus || sub?.status || 'none';
          const price = sub?.actualPrice?.amount || 0;
          const endDate = item.subscriptionEndDate || sub?.endDate || null;
          const daysLeft = item.daysRemaining || calculateDaysLeft(endDate);
          const tenantId = item.tenantId || sub?.tenantId;
          const isNewTenant = item.isNewTenant || !hasSubscription;
          const displayStatus = !hasSubscription ? 'none' : status;
          const isExpanded = expandedRow === tenantId;
          const isSelected = selectedTenants.includes(tenantId);
          const hasSpecialPrice = sub?.actualPrice?.isDiscounted === true;

          const planVisibility = sub?.planSnapshot?.visibility || 'public';
          const isPrivate = planVisibility === 'private';
          const isAssigned = isPrivate && sub?.planSnapshot?.assignedTenants?.includes(tenantId);

          return (
            <div key={tenantId || `tenant-${Math.random()}`} className="bg-white">
              {/* Row Header */}
              <div 
                className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-slate-50"
                onClick={() => toggleRow(tenantId)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTenant?.(tenantId);
                  }}
                  className="text-slate-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                >
                  {isSelected ? (
                    <CheckSquare className="w-5 h-5 text-indigo-600" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900 truncate">{tenantName}</span>
                    <StatusBadge status={displayStatus} />
                    {isPrivate && isAssigned && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-600 rounded-full border border-purple-200">
                        <Eye className="w-2.5 h-2.5" />
                        Private
                      </span>
                    )}
                    {hasSpecialPrice && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-600 rounded-full border border-emerald-200">
                        <DollarSign className="w-2.5 h-2.5" />
                        Special
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                    <span className="truncate">{hasSubscription ? planName : 'No Plan'}</span>
                    {hasSubscription && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span>{price === 0 ? 'Free' : `₹${price.toLocaleString()}`}</span>
                        {status === 'active' || status === 'trial' ? (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className={`font-medium ${
                              daysLeft <= 7 ? 'text-rose-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {daysLeft}d left
                            </span>
                          </>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewHistory?.({
                        tenantId: tenantId,
                        tenantName: tenantName
                      });
                    }}
                    className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                    title="View History"
                  >
                    <Clock className="w-4 h-4 text-amber-500" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onManage({
                        tenantId: tenantId,
                        tenantName: tenantName,
                        hasSubscription: hasSubscription,
                        planId: sub?.planId || null,
                        planName: planName,
                        status: status,
                        billingCycle: sub?.billingCycle || 'yearly',
                        actualPrice: sub?.actualPrice || { amount: 0 },
                        endDate: endDate,
                        subscription: sub,
                        isNewTenant: isNewTenant
                      });
                    }}
                    className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4 text-indigo-500" />
                  </button>
                  
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 bg-slate-50/50">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Tenant ID</p>
                      <p className="text-sm text-slate-700 font-medium">{tenantId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Plan</p>
                      <p className="text-sm text-slate-700 font-medium">{planName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Price</p>
                      <p className="text-sm text-slate-700 font-medium">
                        {!hasSubscription ? '-' : price === 0 ? 'Free' : `₹${price.toLocaleString()}`}
                        {hasSpecialPrice && (
                          <span className="ml-1 text-xs text-emerald-600">(Special)</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Expires</p>
                      <p className="text-sm text-slate-700 font-medium">
                        {!hasSubscription ? '-' : endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Actions */}
                  <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                    {/* ✅ SPECIAL PRICE - Always show */}
                    <button
                      onClick={() => {
                        onSetSpecialPrice({
                          tenantId: tenantId,
                          tenantName: tenantName,
                          planId: sub?.planId || null,
                          planName: planName,
                          currentPrice: price,
                          hasSpecialPrice: hasSpecialPrice,
                          hasSubscription: hasSubscription
                        });
                        setExpandedRow(null);
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1"
                    >
                      <DollarSign className="w-3.5 h-3.5" />
                      {hasSpecialPrice ? 'Update Special' : 'Set Special'}
                    </button>

                    {/* ✅ REMOVE SPECIAL PRICE - Only if exists */}
                    {hasSpecialPrice && (
                      <button
                        onClick={() => {
                          handleRemoveSpecialPrice(tenantId, sub?.planId);
                          setExpandedRow(null);
                        }}
                        disabled={isProcessing}
                        className="px-3 py-1.5 text-xs font-medium bg-rose-50 text-rose-700 rounded-lg hover:bg-rose-100 transition-colors flex items-center gap-1 disabled:opacity-50"
                      >
                        <X className="w-3.5 h-3.5" />
                        {isProcessing ? 'Removing...' : 'Remove Special'}
                      </button>
                    )}

                    {!hasSubscription ? (
                      <>
                        <button
                          onClick={() => {
                            onManage({
                              tenantId: tenantId,
                              tenantName: tenantName,
                              hasSubscription: false,
                              isNewTenant: true
                            });
                            setExpandedRow(null);
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors flex items-center gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Assign Plan
                        </button>
                        <button
                          onClick={() => {
                            onSetTrial(tenantId, 14);
                            setExpandedRow(null);
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-1"
                        >
                          <Gift className="w-3.5 h-3.5" />
                          Trial
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            onExtendQuick(tenantId, 30);
                            setExpandedRow(null);
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                          +30 Days
                        </button>
                        <button
                          onClick={() => {
                            onSetTrial(tenantId, 14);
                            setExpandedRow(null);
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-1"
                        >
                          <Gift className="w-3.5 h-3.5" />
                          Trial
                        </button>
                        {isPrivate && isAssigned && (
                          <button
                            onClick={() => {
                              handleUnassign(tenantId, sub.planId);
                              setExpandedRow(null);
                            }}
                            disabled={isProcessing}
                            className="px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-1"
                          >
                            <X className="w-3.5 h-3.5" />
                            Unassign Private
                          </button>
                        )}
                        <button
                          onClick={() => {
                            onDeactivate(tenantId);
                            setExpandedRow(null);
                          }}
                          className="px-3 py-1.5 text-xs font-medium bg-rose-50 text-rose-700 rounded-lg hover:bg-rose-100 transition-colors flex items-center gap-1"
                        >
                          <Ban className="w-3.5 h-3.5" />
                          Deactivate
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubscriptionTable;